import { useEffect, useState } from "react";
import "./Home.css";
import ApiService from "../../components/Api_services";
import HomeBar from "../../components/home_v2/HomeBar";
import HomeBar2 from "../../components/home_v2/HomeBar2";
import HomeBanner from "../../components/home_v2/HomeBanner";
// Arrow icon replaced by lucide ChevronRight
import { Link } from "react-router-dom";
import FallbackImg from "../../components/FallbackImg";
import BarTimer from "./BarTimer";
import { useSelector } from "react-redux";
import { ACCESS_KEYS, BRAND } from "../../constants";
import { hasAccess } from "../../utils/Permission";
import { Shield, Package2, Truck, Clock, ChevronRight } from "lucide-react";
import Announcement from "../../components/announcement/Announcement";

const Homev2 = ({ setCategoryDropdownOpen, setSelectedCategoryCode }) => {
  //bar list
  const [showTutorial, setShowTutorial] = useState(false);
  const [showMemo, setShowMemo] = useState(false);
  const [currentMemoIndex, setCurrentMemoIndex] = useState(0);
  const [bars, setBars] = useState([]);
  const [memos, setMemos] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [eventDate, setEventDate] = useState("");
  const [error, setError] = useState(null);
  const [posters, setPosters] = useState([]);
  const [topSales, setTopSales] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const sub_role = useSelector((state) => state.auth.sub_role) || [];
  const is_subuser =
    useSelector((state) => state.auth.user?.is_subuser) || false;
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const canViewHome = !is_subuser || hasAccess(sub_role, ACCESS_KEYS.HOME);

  const fetchMemo = async () => {
    try {
      const memoData = await ApiService.getMemo();
      if (memoData?.status === 200 && Array.isArray(memoData.data)) {
        const now = new Date();
        const validMemos = memoData.data.filter((memo) => {
          const validFrom = new Date(memo.valid_date_from);
          const validTo = new Date(memo.valid_date_to);
          validTo.setHours(23, 59, 59, 999);
          const isDateValid =
            memo.is_permanent === 1 || (now >= validFrom && now <= validTo);
          const isDismissed =
            localStorage.getItem(`hidePromoMemo_${memo.id}`) ===
            memo.updated_date;
          return isDateValid && !isDismissed;
        });
        setMemos(validMemos);
        if (validMemos.length > 0) {
          setCurrentMemoIndex(0);
          setShowMemo(true);
        }
      }
    } catch (err) {
      console.error("API Error:", err);
      setMemos([]);
      setShowMemo(false);
    }
  };

  const handleMemoClose = () => {
    if (currentMemoIndex < memos.length - 1) {
      setCurrentMemoIndex((prev) => prev + 1);
    } else {
      setShowMemo(false);
    }
  };

  const handleMemoDontShow = (memo) => {
    localStorage.setItem(`hidePromoMemo_${memo.id}`, memo.updated_date);
    handleMemoClose();
  };

  const fetchPosters = async () => {
    try {
      const posterData = await ApiService.getAllPoster();
      // 假设返回结构为 { data: { poster: [...] } }
      setPosters(posterData?.data?.poster || []);
    } catch (err) {
      console.error("API Error:", err);
      setPosters([]);
    }
  };

  const fetchBars = async () => {
    try {
      const barData = await ApiService.getAllBar();
      if (barData?.status === 200 && barData.data) {
        const barsArr = barData?.data?.bar || [];
        setBars(barsArr);
        const limitedBar = barsArr.find(
          (bar) => bar.limited_time === "1" && bar.live_period_to,
        );
        setEventDate(limitedBar ? limitedBar.live_period_to : "");
      }
    } catch (err) {
      console.error("API Error:", err);
      setBars([]);
      setError("Failed to fetch home data");
    }
  };

  const checkTutorial = async () => {
    try {
      const response = await ApiService.getTutorialStatus();
      if (!response || !response.data) {
        console.warn("Tutorial API returned empty data");
        return;
      }

      const status = response.data.philips_tutorial_status;

      if (status === 0 || status === "0") {
        setShowTutorial(true);
      }
    } catch (err) {
      console.error("Failed to check tutorial status", err);
    }
  };

  // load top 6 product groups for home
  const fetchTopSix = async () => {
    try {
      const res = await ApiService.getTopSixProduct(BRAND);
      let list = [];
      if (res) {
        if (Array.isArray(res)) {
          list = res;
        } else if (Array.isArray(res.data?.product)) {
          list = res.data.product;
        } else if (Array.isArray(res.product)) {
          list = res.product;
        } else if (Array.isArray(res.data)) {
          list = res.data;
        } else if (res.data && typeof res.data === "object") {
          list = Object.values(res.data);
        } else if (typeof res === "object") {
          list = Object.values(res);
        }
      }
      if (!Array.isArray(list)) list = [];
      setProductGroups(list);
    } catch (err) {
      console.error("Failed to load product groups", err);
    }
  };

  // load fast moving (top sales) products
  const fetchTopSales = async () => {
    try {
      const res = await ApiService.getTopSalesProduct(BRAND);
      let list = [];
      if (res) {
        if (Array.isArray(res)) list = res;
        else if (Array.isArray(res.data)) list = res.data;
        else if (Array.isArray(res.data?.data)) list = res.data.data;
        else if (Array.isArray(res.data?.product)) list = res.data.product;
        else if (res.data && typeof res.data === "object")
          list = Object.values(res.data);
      }
      if (!Array.isArray(list)) list = [];
      setTopSales(list);
    } catch (err) {
      console.error("Failed to load top sales products", err);
    }
  };

  useEffect(() => {
    let interval = null;

    const fetchHomeData = () => {
      fetchBars();
      fetchPosters();
      fetchMemo();
      setTimeout(() => {
        checkTutorial();
      }, 300);
      fetchTopSix();
      fetchTopSales();
    };

    if (!isAuthenticated) {
      fetchHomeData();
      return;
    }

    const tryFetch = () => {
      const token = localStorage.getItem("access_token");

      if (!token) {
        // console.log("[Home] No token in localStorage yet, waiting...");
        return;
      }

      // console.log("[Home] Token found in localStorage, fetching data...");
      clearInterval(interval); // Stop polling once token is found
      fetchHomeData();
    };

    interval = setInterval(tryFetch, 100); // check every 100ms

    return () => clearInterval(interval); // cleanup on unmount
  }, [isAuthenticated]);

  // const getValidProducts = (products) => {
  //   const isLoggedIn = !!localStorage.getItem("access_token");
  //   return (products || []).filter((product) => {
  //     let mainPrice = 0;
  //     if (isLoggedIn) {
  //       const lowestPrice = parseFloat(product.lowest_price || 0);
  //       const brandPrice = parseFloat(product.brand_price || 0);
  //       const brandPriceOffer = parseFloat(product.brand_price_offer || 0);
  //       if (lowestPrice > 0 && lowestPrice < brandPrice) {
  //         mainPrice = lowestPrice;
  //       } else if (brandPrice > 0 && brandPriceOffer < brandPrice) {
  //         mainPrice = brandPriceOffer;
  //       } else {
  //         mainPrice = brandPriceOffer;
  //       }
  //     } else {
  //       mainPrice = product.rcp;
  //     }
  //     return mainPrice && mainPrice > 0;
  //   });
  // };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!canViewHome) {
    return <div style={{ minHeight: "80vh" }}></div>;
  }

  // Function to render the content bar
  const renderViewMoreButton = (bar, products = []) => {
    // Use bar-specific title/description when available. Do NOT use event_title_name.
    const title = (bar && bar.bar_title) || "";
    const description = (bar && bar.bar_description) || "";

    return (
      <div className="contentbar_v2">
        <div className="contentbar-title-container_v2">
          <div className="contentbar-title_v2">
            <div style={{ fontSize: 24, fontWeight: 700 }}>{title}</div>
            <div
              style={{
                fontSize: 16,
                color: "rgba(255,255,255,0.9)",
                marginTop: 6,
              }}
            >
              {description}
            </div>
          </div>
        </div>
        {products.length > 0 && (
          <>
            <div className="cdtimer-container_v2">
              {bar && bar.limited_time === "1" && bar.live_period_to && (
                <BarTimer live_period_to={bar.live_period_to} />
              )}
            </div>

            <div className="viewmore-container_v2">
              <Link
                to={`/event/${bar?.event_id || ""}`}
                className="viewmore_v2"
              >
                View All{" "}
              </Link>
            </div>
          </>
        )}
      </div>
    );
  };

  const handleDontShowTutorial = async () => {
    setShowTutorial(false);
    try {
      await ApiService.updateTutorialStatus();
    } catch (err) {
      console.error("Failed to update tutorial status", err);
    }
  };

  const features = [
    { title: "Genuine Products", subtitle: "100% Authentic" },
    { title: "Bulk Discounts", subtitle: "Volume pricing available" },
    { title: "Fast Fulfillment", subtitle: "Quick delivery" },
    { title: "Dealer Support", subtitle: "Dedicated assistance" },
  ];

  const icons = [Shield, Package2, Truck, Clock];

  const getProductLink = (item) => {
    if (
      typeof item.item_id === "string" &&
      item.item_id.startsWith("is_package_")
    ) {
      const parts = item.item_id.split("_"); // ["is", "package", "13", "1"]
      const packageId = parts[2];
      const packageType = parts[3];

      if (packageType === "1") return `/grouppackagedetail/${packageId}`;
      if (packageType === "0") return `/packageproductdetail/${packageId}`;
    }

    if (item.bundle_id != null && item.bundle_id.includes("is_bundle")) {
      const bundleId = item.bundle_id.replace("is_bundle_", "");
      return `/productdetail/${item.item_id}?isBundle=1&bundleId=${bundleId}`;
    }

    return `/productdetail/${item.item_id}`;
  };

  return (
    <div className="home_v2">
      {error && <div className="error-container_v2">{error}</div>}

      {!error && (
        <div className="content_v2">
          <HomeBanner />

          <div className="feature-row-wrapper_v2">
            <div className="feature-row_v2 container_v2">
              {features.map((f, i) => {
                const Icon = icons[i];
                return (
                  <div className="feature-card_v2" key={`feature-${i}`}>
                    <div className="feature-card-icon_v2">
                      {Icon ? (
                        <Icon size={32} strokeWidth={2} color="#283E24" />
                      ) : (
                        <div className="icon-placeholder_v2" aria-hidden />
                      )}
                    </div>
                    <div className="feature-card-text_v2">
                      <div className="feature-title_v2">{f.title}</div>
                      <div className="feature-subtitle_v2">{f.subtitle}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* <div className="contentbar1_v2">
            <ul>
              <li>
                <a href="#category">
                  <FallbackImg src={Lock} alt="lock" width="25" height="25" />
                  Security Guarantees
                </a>
              </li>
              <li>
                <a href="#newarrival">
                  <FallbackImg
                    src={ShieldCheck}
                    alt="shieldcheck"
                    width="25"
                    height="25"
                  />
                  Verified Business
                </a>
              </li>
              <li>
                <a href="#promotion">
                  <FallbackImg src={Truck} alt="truck" width="25" height="25" />
                  Delivery Guarantee
                </a>
              </li>
              <li>
                <a href="#preorder">
                  <FallbackImg
                    src={Headset}
                    alt="headset"
                    width="25"
                    height="25"
                  />
                  24/7 support
                </a>
              </li>
            </ul>
          </div> */}

          {/* Tutorial Video popup */}
          {showTutorial && (
            <div
              className="tutorial-modal_v2"
              onClick={() => setShowTutorial(false)}
            >
              <div
                className="tutorial-modal-content_v2"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="tutorial-modal-close_v2"
                  onClick={() => setShowTutorial(false)}
                >
                  &#10006;
                </button>

                <div style={{ padding: "24px" }}>
                  <div className="tutorial-model-title_v2">Tutorial Video</div>
                  <iframe
                    width="700px"
                    height="400px"
                    src="https://www.youtube.com/embed/v_wBUwZsXCE"
                    title="Tutorial Vedio"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>

                <button
                  className="tutorial-modal-dontshow_v2"
                  onClick={handleDontShowTutorial}
                >
                  Don't show again
                </button>
              </div>
            </div>
          )}

          {/* Promotion Memo 弹窗 */}
          {showMemo && memos.length > 0 && (
            <Announcement
              memo={memos[currentMemoIndex]}
              current={currentMemoIndex + 1}
              total={memos.length}
              onClose={handleMemoClose}
              onDontShow={handleMemoDontShow}
            />
          )}

          {/* Bar and Poster are merged into one list and sorted by display_sequence
              (a single shared order the admin sets in Promotion Setting > Home
              Display Sort — see unity_api Home::applyHomeDisplaySort), so Bar and
              Poster sections render interleaved in that exact order instead of
              always "all bars, then all posters". Items with no saved order (no
              display_sequence) keep the order the API already returned them in. */}
          {[
            ...bars.map((bar, index) => ({
              type: "bar",
              key: `bar1-${bar.event_id || index}`,
              data: bar,
              sequence: bar.display_sequence ?? Number.MAX_SAFE_INTEGER,
            })),
            ...posters.map((poster, index) => ({
              type: "poster",
              key: `poster-${poster.id || index}`,
              data: poster,
              sequence: poster.display_sequence ?? Number.MAX_SAFE_INTEGER,
            })),
          ]
            .sort((a, b) => a.sequence - b.sequence)
            .map((section) => {
              if (section.type === "bar") {
                const bar = section.data;
                return (
                  <HomeBar
                    key={section.key}
                    products={bar.item}
                    renderViewMoreButton={() =>
                      renderViewMoreButton(bar, bar.item)
                    }
                  />
                );
              }
              const poster = section.data;
              return (
                <HomeBar2
                  key={section.key}
                  products={poster.item}
                  titleImage={poster.poster_photo_url}
                  eventId={poster.event_id}
                  itemQuantity={poster.item_quantity}
                />
              );
            })}

          {/* Fast Moving Items row above product groups */}
          {topSales && topSales.length > 0 && (
            <div className="fast-moving-row_v2 container_v2">
              <h3 className="fast-title_v2">Fast Moving Items</h3>
              <div className="cards_v2">
                {topSales.slice(0, 6).map((p, idx) => {
                  const itemId = p.item_id || p["item_id"] || `it-${idx}`;
                  const code = p.item_code || p["item_code"] || "";
                  const name = p.item_name || p["item_name"] || "";
                  const photo =
                    p.item_photo_url ||
                    p["item_photo_url"] ||
                    p.photo_url ||
                    "";

                  const isLoggedIn = !!isAuthenticated;
                  const rcp = parseFloat(p.rcp || 0) || 0;
                  const brandPrice =
                    p.brand_price !== null && p.brand_price !== undefined
                      ? parseFloat(p.brand_price)
                      : null;
                  const lowest =
                    p.lowest_price !== undefined && p.lowest_price !== null
                      ? parseFloat(p.lowest_price)
                      : null;
                  const highest =
                    p.highest_price !== undefined && p.highest_price !== null
                      ? parseFloat(p.highest_price)
                      : null;

                  // discount value from backend (brand price / rcp)
                  const discountBP =
                    p.discount_percent_bp || p.discount_percent || "";
                  const discountRCP =
                    p.discount_percent_rcp || p.display_discount_pct || "";

                  let showRange = false;
                  let priceNowText = "";
                  let showOriginal = false;
                  let originalPrice = 0;
                  let displayNumeric = null;

                  if (isLoggedIn) {
                    if (
                      !isNaN(lowest) &&
                      !isNaN(highest) &&
                      lowest > 0 &&
                      highest > 0 &&
                      highest > lowest // strictly greater, matches reference's isPriceRange
                    ) {
                      showRange = true;
                      priceNowText = `RM ${lowest.toFixed(2)} - RM ${highest.toFixed(2)}`;
                      showOriginal = false;
                      displayNumeric = lowest;
                    } else if (lowest > 0) {
                      // Non-range case: lowest_price is the actual selling price
                      priceNowText = `RM ${lowest.toFixed(2)}`;
                      displayNumeric = lowest;

                      // original/strikethrough price is brand_price, not rcp
                      originalPrice = brandPrice ?? 0;
                      showOriginal =
                        brandPrice !== null &&
                        brandPrice !== undefined &&
                        Number(brandPrice) > 0 &&
                        Number(lowest) < Number(brandPrice);
                    } else {
                      // Fallback when lowest_price is missing/zero
                      const usePrice = brandPrice ?? rcp;
                      priceNowText = `RM ${Number(usePrice).toFixed(2)}`;
                      displayNumeric = Number(usePrice);
                      showOriginal = false;
                    }
                  } else {
                    const usePrice = brandPrice ?? lowest ?? rcp;
                    priceNowText = `RM ${Number(usePrice).toFixed(2)}`;
                    showOriginal = false;
                    displayNumeric = Number(usePrice);
                  }

                  // Compute discount percentages if backend didn't provide them
                  const computedBP =
                    brandPrice !== null &&
                    brandPrice !== undefined &&
                    displayNumeric !== null &&
                    Number(brandPrice) > Number(displayNumeric)
                      ? `-${Math.round(((Number(brandPrice) - Number(displayNumeric)) / Number(brandPrice)) * 100)}%`
                      : "";

                  const computedRCP =
                    rcp &&
                    displayNumeric !== null &&
                    Number(rcp) > Number(displayNumeric)
                      ? `-${Math.round(((Number(rcp) - Number(displayNumeric)) / Number(rcp)) * 100)}%`
                      : "";

                  // Prefer backend-provided discount strings, otherwise use computed values
                  const discountDisplay = isLoggedIn
                    ? discountBP || computedBP
                    : discountRCP || computedRCP;
                  const showDiscount =
                    !!discountDisplay && discountDisplay !== "0%";

                  return (
                    <Link
                      key={itemId}
                      to={getProductLink(p)}
                      className="fast-card_v2"
                    >
                      {p.promo &&
                      Array.isArray(p.promo) &&
                      p.promo.length > 0 ? (
                        <div class="promo-tag-overlay_v2">
                          <div className="promo-tag-text_v2">Promo</div>
                        </div>
                      ) : null}

                      {showDiscount ? (
                        <div className="discount-pill_v2">
                          {discountDisplay}
                        </div>
                      ) : null}

                      <div className="pd-alsolikecard-inner">
                        <div className="pd-alsolikecard-image">
                          <FallbackImg
                            src={photo}
                            alt={name}
                            className="main-product-image"
                          />
                        </div>
                        <div className="pd-alsolikecard-info">
                          <div className="pd-alsolikecard-info-code">
                            {code}
                          </div>
                          <div className="pd-alsolikecard-info-name">
                            {name}
                          </div>
                          <div className="pd-productprice-container-v2">
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#ff6900",
                                fontWeight: "600",
                              }}
                            >
                              {priceNowText}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {productGroups && productGroups.length > 0 && (
            <div className="product-group-row_v2 container_v2">
              <div className="row-header_v2">
                <h3>Shop by Product</h3>
                <Link to="/exploreproduct" className="view-more_v2">
                  View More{" "}
                  <ChevronRight size={20} strokeWidth={2} color="#283E24" />
                </Link>
              </div>

              <div className="groups_v2">
                {productGroups.map((g, i) => {
                  const code = g.code || g["code"] || `grp-${i}`;
                  const name = g.name || g["name"] || "";
                  const photo = g.photo_url || g["photo_url"] || "";
                  return (
                    <Link
                      to={`/productlist?product=${encodeURIComponent(code)}&page=1`}
                      key={code + i}
                      className="group-card_v2"
                      tabIndex={0}
                    >
                      <div className="group-image_v2">
                        <FallbackImg src={photo} alt={name} />
                      </div>
                      <div className="group-name_v2">{name}</div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {<div style={{ paddingBottom: "30px" }}></div>}
        </div>
      )}
    </div>
  );
};

export default Homev2;
